/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */
import { Injectable } from '@angular/core';
import { EsidDisambiguationTicketActions, IESIDData, IESIDDataItem, Ticket } from '@touchplan/common';
import { Swimlane } from 'src/app/app-data/models/Swimlane';
import {
	ChecklistItemId,
	IChecklistItemDataWithId,
} from 'src/app/shared/ui/material/checkbox-list/checkbox-list-model';
import { getSwimlaneBelonging } from 'src/app/utils/ticket-utils';

export interface IEsidChecklistItemData extends IChecklistItemDataWithId {
	originalModel?: {
		esid: string;
		ticket: Ticket;
		swimlaneBelonging: Swimlane;
		isCreateCheckBox: boolean;
		showHeader: boolean;
	};
}

export class EsidChecklistDataMap extends Map<ChecklistItemId, IEsidChecklistItemData> {}

@Injectable()
export class EsidDisambiguationService {
	constructor() {}

	/**
	 * Generates or updates a checklist for ESID disambiguation based on provided data.
	 *
	 * @param esidDisambiguation - An object containing ESID data with associated tickets.
	 * @param ticketList - A map of ticket IDs to Ticket objects.
	 * @param swimlaneList - A map of swimlane IDs to Swimlane objects.
	 * @param currentEsidDisambiguationCheckList - (Optional) An existing checklist to update.
	 * @returns A map representing the ESID disambiguation checklist.
	 */
	public getEsidDisambiguationCheckList(
		esidDisambiguation: IESIDData,
		ticketList: Map<string, Ticket>,
		swimlaneList: Map<string, Swimlane>,
		currentEsidDisambiguationCheckList?: EsidChecklistDataMap
	): EsidChecklistDataMap {
		const esidDisambiguationCheckList =
			currentEsidDisambiguationCheckList || new Map<string, IChecklistItemDataWithId>();
		for (const [esid, disambiguation] of Object.entries(esidDisambiguation)) {
			if (!disambiguation.tickets) {
				continue;
			}
			Object.entries(disambiguation.tickets).forEach(([ticketId, _], index) => {
				const ticket = ticketList.get(ticketId);
				if (!ticket) {
					return;
				}
				const updateTicketComposeId = `${esid}.${ticketId}`;
				const swimlaneBelonging = getSwimlaneBelonging(ticket.view.top, swimlaneList);
				const esidDisambiguationItem = esidDisambiguationCheckList.get(updateTicketComposeId);
				if (esidDisambiguationItem) {
					esidDisambiguationItem.originalModel.ticket = ticket;
					esidDisambiguationItem.originalModel.swimlaneBelonging = swimlaneBelonging;
				} else {
					esidDisambiguationCheckList.set(updateTicketComposeId, {
						id: updateTicketComposeId,
						isChecked: false,
						originalModel: {
							esid,
							ticket,
							swimlaneBelonging,
							isCreateCheckBox: false,
							showHeader: index === 0,
						},
					});
				}
			});
		}
		return esidDisambiguationCheckList;
	}

	/**
	 * Processes the ESID disambiguation checklist and returns a map of ESID data.
	 *
	 * @param esidDisambiguationCheckList - The checklist data map containing ESID disambiguation information.
	 * @returns A map of ESID data where each key is an ESID and the value is an object containing the disambiguation details.
	 */
	public getEsidDisambiguation(esidDisambiguationCheckList: EsidChecklistDataMap): IESIDData {
		const esidDisambiguationSelection: IESIDData = {};
		esidDisambiguationCheckList.forEach((item) => {
			const esid = item.originalModel?.esid;
			const entry: IESIDDataItem = esidDisambiguationSelection[esid] ?? {
				createNew: false,
				tickets: {},
				externalScheduleId: esid,
			};
			if (item.originalModel?.isCreateCheckBox) {
				entry.createNew = item.isChecked;
			} else {
				entry.tickets[item.originalModel?.ticket.$id] = {
					action: item.isChecked
						? EsidDisambiguationTicketActions.Update
						: EsidDisambiguationTicketActions.Ignore,
				};
			}
			esidDisambiguationSelection[esid] = entry;
		});
		return esidDisambiguationSelection;
	}
}
